import React, { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import api from "../../api";

// Task spec §14/§15: a single-employee search-and-select, reused by (1) the
// KRA/KPI "assign configuration to an employee" picker
// (GET /admin/kra-kpi/employees/search) and (2) the Form Builder's
// subject-field option editor -- "which real employee does this dropdown
// option represent" (GET /forms/employee-options/search). Never asks anyone
// to type an option_code/employee id by hand; the caller always receives the
// canonical users.id via onChange.
const PAGE_SIZE = 50;

function initialOf(name) {
  const trimmed = String(name || "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

function toOption(u) {
  return { value: u.id, label: u.name || `User #${u.id}`, user: u };
}

// Knowledge Sharing subject-selection hardening, requirement 1: two live
// accounts can legitimately share a near-identical display name (e.g.
// "Ratana" vs "Ms. Ratana Dubey", the real ticket #106/#18 case) --
// email/username alone was already enough to tell them apart in most cases,
// but designation/department (additive, only present when the caller's
// endpoint supplies them -- see services/employeeDirectoryService.js) makes
// that disambiguation explicit rather than relying on the admin noticing a
// subtle email difference.
function identityLine(u) {
  const parts = [u.email || u.username || null, u.designation_name || null, u.department_name || null].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

const Option = (props) => {
  const { data } = props;
  const u = data.user;
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: "50%", background: "#6d1530", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0,
        }}>
          {initialOf(u.name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {u.name || `User #${u.id}`}
          </div>
          <div style={{ fontSize: 11, color: "#6c7891", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {identityLine(u)}
          </div>
        </div>
      </div>
    </components.Option>
  );
};

const SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 40 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

export default function EmployeePicker({ endpoint, value = null, onChange, placeholder = "Search employee by name, email or username...", isDisabled = false }) {
  const [rawUsers, setRawUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [knownUsers, setKnownUsers] = useState(new Map());
  const requestSeq = useRef(0);

  const fetchUsers = async (searchText) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(endpoint, { params: { search: searchText || undefined, pageSize: PAGE_SIZE } });
      if (seq !== requestSeq.current) return;
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setRawUsers(rows);
      setKnownUsers((prev) => {
        const next = new Map(prev);
        rows.forEach((u) => next.set(String(u.id), u));
        return next;
      });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err?.response?.data?.message || "Failed to load employees.");
      setRawUsers([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(search), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, endpoint]);

  const options = rawUsers.map(toOption);
  const selectedUser = value != null ? knownUsers.get(String(value)) : null;
  const selectedOption = selectedUser ? toOption(selectedUser) : (value != null ? { value, label: `User #${value}`, user: { id: value } } : null);

  if (error) {
    return (
      <div className="text-danger small d-flex align-items-center gap-2">
        <span>⚠ {error}</span>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => fetchUsers(search)}>Retry</button>
      </div>
    );
  }

  return (
    <Select
      classNamePrefix="employee-picker"
      isClearable
      isDisabled={isDisabled}
      isLoading={loading}
      styles={SELECT_STYLES}
      options={options}
      value={selectedOption}
      onChange={(opt) => onChange(opt ? opt.value : null, opt ? opt.user : null)}
      onInputChange={(text, meta) => {
        if (meta.action === "input-change") setSearch(text);
        return text;
      }}
      components={{ Option }}
      placeholder={placeholder}
      noOptionsMessage={() => (loading ? "Loading employees..." : search ? "No employees match your search." : "Start typing to search employees.")}
    />
  );
}
